import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { MapView } from "@/components/MapView";
import { MessagesButton } from "@/components/RideMessages";
import { FollowBanner, ShareRideButton, useFollow } from "@/components/FollowRide";
import { CounterpartProfile, useRiderLocation } from "@/components/Profiles";
import { DEFAULT_OPTS as BASE_OPTS, buildTrip, useQuote } from "@/components/booking/useQuote";
import { RideSummary } from "@/components/RideStatus";
import { BottomNav } from "@/components/rider/Nav";
import { Greeting, SummaryCards, UpcomingPreview } from "@/components/rider/Dashboard";
import { BookFlow } from "@/components/rider/BookFlow";
import { EMPTY_LEGS, buildSplit } from "@/components/rider/SplitLegs";
import { MyRides } from "@/components/rider/MyRides";
import { CreditScreen, MessagesScreen, ProfileScreen } from "@/components/rider/Screens";
import { Button } from "@/components/ui/button";
import { PickupTimers } from "@/components/driver/PickupTimer";

const DEFAULT_OPTS = { ...BASE_OPTS, when: "later" };
const START_FLOW = { mode: "now", kind: null, repeat: false, step: "edit" };

function ActivePanel({ ride, follow }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/rides/${ride.id}/cancel`, {});
      toast("Ride cancelled");
      qc.invalidateQueries({ queryKey: ["activeRide"] });
      qc.invalidateQueries({ queryKey: ["myRides"] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="active-ride-panel">
      <h2 className="font-display text-xl font-bold tracking-tight">Your current ride</h2>
      <RideSummary ride={ride} perspective="rider" />
      {ride.driver_id && <CounterpartProfile rideId={ride.id} />}
      {ride.driver_id && <FollowBanner follow={follow} />}
      {follow?.stale && <p className="rounded-xl bg-amber-400/10 px-3 py-2 text-xs text-amber-200" data-testid="driver-location-stale">Driver location is not live right now (last update {follow.location_age_seconds}s ago).</p>}
      {ride.driver_id && follow && follow.in_window && !follow.driver_online && ride.status === "assigned" && <p className="text-xs text-muted-foreground" data-testid="driver-offline-note">Your driver's live location appears when they go Online.</p>}
      <PickupTimers timers={follow?.wait_timers} status={ride.status} scheduledAt={ride.scheduled_at} />
      {ride.amount_due > 0 && (ride.payment_method === "pay_at_pickup" || ride.secondary_payment_method === "pay_at_pickup") && (
        <p className="rounded-xl bg-muted px-3 py-2 text-sm font-semibold" data-testid="active-payment-due">PAYMENT DUE AT PICKUP · ${ride.amount_due.toFixed(2)}</p>
      )}
      {["confirmed", "assigned", "en_route", "arrived", "in_progress"].includes(ride.status) && (
        <div className="grid grid-cols-2 gap-2"><MessagesButton rideId={ride.id} /><ShareRideButton rideId={ride.id} /></div>
      )}
      {["pending", "confirmed", "assigned"].includes(ride.status) && (
        <Button variant="outline" data-testid="cancel-ride-button" onClick={cancel} disabled={busy} className="h-11 w-full rounded-full">Cancel ride</Button>
      )}
    </div>
  );
}

function useBooking() {
  const [flow, setFlow] = useState(START_FLOW);
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [stops, setStops] = useState([]);
  const [legs, setLegs] = useState(EMPTY_LEGS);
  const [opts, setOpts] = useState(DEFAULT_OPTS);
  const split = flow.kind === "split";
  const repeat = flow.mode === "scheduled" && flow.repeat;
  const eff = { ...opts, bookingType: repeat ? "recurring" : flow.kind, recurringKind: flow.kind, when: "later" };
  const trip = !flow.kind ? null : split ? buildSplit(legs, opts, repeat) : buildTrip({ pickup, dropoff, stops, opts: eff });
  const quote = useQuote(trip, undefined, split ? "/rides/split/quote" : "/pricing/quote");
  const reset = useCallback(() => {
    setFlow(START_FLOW); setPickup(null); setDropoff(null); setStops([]); setLegs(EMPTY_LEGS); setOpts(DEFAULT_OPTS);
  }, []);
  return { flow, setFlow, pickup, setPickup, dropoff, setDropoff, stops, setStops, legs, setLegs, opts, setOpts, trip, quote, reset, split };
}

export default function RiderHome() {
  const [tab, setTab] = useState("home");
  const [ridesTab, setRidesTab] = useState("upcoming");
  const b = useBooking();
  const { data: active } = useQuery({ queryKey: ["activeRide"], queryFn: () => api.get("/rides/active").then((r) => r.data), refetchInterval: 4000 });
  const { data: follow } = useFollow(active);
  useRiderLocation(active);
  const { retry } = b.quote;
  useEffect(() => {
    if (!b.trip) return undefined;
    const t = setInterval(retry, 60000);
    return () => clearInterval(t);
  }, [!!b.trip, retry]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (t, sub) => {
    setTab(t);
    if (t === "rides" && sub) setRidesTab(sub);
    if (sub === "owed") setTimeout(() => document.getElementById("owed")?.scrollIntoView({ behavior: "smooth" }), 300);
  };
  const movePin = async (which, { lat, lng }) => {
    const leg = b.split ? b.legs[0] : null;
    const current = leg ? leg[which] : which === "pickup" ? b.pickup : b.dropoff;
    const set = leg ? (p) => b.setLegs((ls) => [{ ...ls[0], [which]: p }, ls[1]]) : which === "pickup" ? b.setPickup : b.setDropoff;
    try {
      const { data } = await api.get("/geo/reverse", { params: { lat, lng } });
      if (!data.resolved) toast.warning("Pin moved, but we couldn't match a street address there.");
      set({ ...data, location_source: current.location_source, unit_number: current.unit_number, pin_adjusted: true,
        name: data.resolved && data.name !== "Current location" ? data.name : data.resolved ? data.formatted_address.split(",")[0] : "Pinned location" });
    } catch (e) {
      toast.error(formatApiError(e));
      set({ ...current });
    }
  };
  const mp = b.split ? b.legs[0].pickup : b.pickup;
  const md = b.split ? b.legs[0].dropoff : b.dropoff;
  const map = active
    ? { pickup: active.pickup, dropoff: active.dropoff, route: follow?.driver_route?.length ? follow.driver_route : active.route, driver: follow?.driver_location }
    : { pickup: mp, dropoff: md, route: b.quote.data?.route, onMove: movePin };
  const home = tab === "home";

  return (
    <div data-testid="rider-home">
      <MapView {...map} />
      <TopBar />
      <section data-testid="ride-sheet"
        className={`sheet fade-up fixed inset-x-0 bottom-[60px] z-30 overflow-y-auto rounded-t-3xl p-5 pb-8 md:bottom-[76px] md:left-6 md:right-auto md:top-24 md:w-[440px] md:rounded-3xl md:p-6 ${home ? "top-[34vh]" : "top-20"}`}>
        {home && (
          <div className="space-y-5">
            <Greeting onProfile={() => setTab("profile")} />
            <SummaryCards go={go} />
            {active && <ActivePanel ride={active} follow={follow} />}
            {(
              <BookFlow flow={b.flow} setFlow={b.setFlow} places={{ pickup: b.pickup, setPickup: b.setPickup, dropoff: b.dropoff, setDropoff: b.setDropoff }}
                stops={b.stops} setStops={b.setStops} legs={b.legs} setLegs={b.setLegs} opts={b.opts} setOpts={b.setOpts} trip={b.trip} quote={b.quote}
                onBooked={() => { b.reset(); go("rides", "upcoming"); }} />
            )}
            <UpcomingPreview go={go} onFollow={() => setTab("home")} />
          </div>
        )}
        {tab === "rides" && <MyRides tab={ridesTab} setTab={setRidesTab} onFollow={() => setTab("home")} />}
        {tab === "credit" && <CreditScreen />}
        {tab === "messages" && <MessagesScreen />}
        {tab === "profile" && <ProfileScreen />}
      </section>
      <BottomNav value={tab} onChange={setTab} />
    </div>
  );
}
